import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  PageWrapper,
  PageHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { EMAILS } from '../../../constants';

import { getCreationResults } from '../../../services/api/creations';
import { getTemplate } from '../../../services/api/emails';
import { getUploadedFaces } from '../../../services/api/faces';
import { getReviewData } from '../../../services/videoBuilderService';

import { useProjectStore } from '../../../stores/project';
import { useProjectSettingsStore } from '../../../stores/projectSettings';

import { Person, HlReel, Shot, SGTemplate, PageError } from '../../../types';
import { isEmpty } from '../../../utils';

import MaClipsBlock from './components/maClipsBlock';
import MaPhotosBlock from './components/maPhotosBlock';

const MissingAssets = () => {
  const { eventId = '' } = useParams();

  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
  } = useProjectStore();
  const { footer, setFooter } = useProjectSettingsStore();

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  const [isAnalysed, setIsAnalysed] = useState(false);

  const [uploadedPeople, setUploadedPeople] = useState<Person[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [hlReel, setHlReel] = useState<HlReel | null>(null);
  const [requiredShots, setRequiredShots] = useState<Shot[]>([]);

  const [photosTemplate, setPhotosTemplate] = useState<SGTemplate>({});
  const [clipsTemplate, setClipsTemplate] = useState<SGTemplate>({});

  // set event id on mount
  useEffect(() => {
    if (eventIdLocal !== eventId) setEventId(eventId);
  }, [eventId, eventIdLocal, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      await fetchData();

      setClipsTemplate(await getTemplate(EMAILS.MISSING_CLIPS));
      setPhotosTemplate(await getTemplate(EMAILS.MISSING_PHOTOS));
      if (isEmpty(footer)) await setFooter();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
    } else if (currentProject) onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, projectError]);

  const fetchData = async () => {
    try {
      const uploadedPeople = await getUploadedFaces(eventId);
      setUploadedPeople(uploadedPeople);

      const { people, hlReel, requiredShots, isAnalysed } = await getReviewData(
        { eventId },
      );
      const generatedPeople = await getCreationResults(eventId);

      if (isAnalysed) {
        setIsAnalysed(true);

        const generatedPeopleIds = generatedPeople.map((p) => p.face.elementId);
        const peopleTemp = people.filter(
          (p) => !generatedPeopleIds.includes(p.face.elementId),
        );

        setPeople(peopleTemp);
        setHlReel(hlReel);
        setRequiredShots(requiredShots);
      }
    } catch (err) {
      console.error(`Error fetching some data: ${err}`);
      setError('Error fetching some data');
    }
  };

  const { isUploaded, peopleWithoutPhoto } = useMemo(() => {
    const peopleWithoutPhoto = uploadedPeople.filter((person) =>
      isEmpty(person?.files?.filter((file) => file.fileType === 'image')),
    );

    return { isUploaded: !isEmpty(uploadedPeople), peopleWithoutPhoto };
  }, [uploadedPeople]);

  return (
    <PageWrapper
      type="project"
      title={`Missing Assets – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <div className="flex flex-col gap-6">
        <PageHeader
          heading="Missing Assets"
          subheading="Request missing assets from your attendees so that everybody can get a personalised highlight reel!"
        />

        <MaPhotosBlock
          isUploaded={isUploaded}
          people={peopleWithoutPhoto}
          emailTemplate={photosTemplate}
        />

        <MaClipsBlock
          isAnalysed={isAnalysed}
          people={people}
          hlReel={hlReel}
          requiredShots={requiredShots}
          emailTemplate={clipsTemplate}
        />
      </div>

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default MissingAssets;
